import { ReactNode } from "react";

import { SxProps, Theme, useTheme } from "@mui/material";
import { AvailablePaymentCurrency, formatNarrowCurrencyAmount, getDollarValue } from "@simplyk/common";

import { FlipCard } from "./FlipCard";
import { EncouragementTier, getEncouragementTier } from "./getEncouragementTier";
import { useIntroProgress } from "./useIntroProgress";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { FormThermometer, FormThermometerMessage } from "@/forms-design-system/FormThermometer/FormThermometer";
import { HeartsFormThermometer } from "@/forms-design-system/HeartsFormThermometer/HeartsFormThermometer";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { EnvironmentColored, HeartsColored, SparksColored } from "@/icons/special-themes";

export type FormCampaignTargetThermometerProps = {
  id?: string;
  currentCollectedAmount: number;
  targetedAmount: number;
  currency: AvailablePaymentCurrency;
  selectedAmount?: number;
  isIntroAnimated?: boolean;
  label: {
    renderCurrentAmount: (currentCollectedAmount: string, targetedAmount: string) => ReactNode;
    ongoingCampaign0To49: ReactNode;
    ongoingCampaign50To79: ReactNode;
    ongoingCampaign80To99: ReactNode;
    campaignAchieved: ReactNode;
    encouragement?: {
      incredible: ReactNode;
      inspiring: ReactNode;
      almostThere: ReactNode;
      beautifulStart: ReactNode;
      meansSoMuch: ReactNode;
    };
  };
  sx?: SxProps<Theme>;
};

export const FormCampaignTargetThermometer = ({
  id,
  currentCollectedAmount,
  targetedAmount,
  currency,
  selectedAmount = 0,
  isIntroAnimated = false,
  label: labelProp,
  sx,
}: FormCampaignTargetThermometerProps) => {
  const theme = useTheme();
  const { isoLocale } = useLocaleContext();
  const introProgress = useIntroProgress(isIntroAnimated);

  if (!theme.palette.forms || !theme.palette.formsComponents?.thermometer) {
    return <InvalidThemeErrorMessage />;
  }

  const formatAmount = (amount: number) =>
    formatNarrowCurrencyAmount(getDollarValue(amount), { locale: isoLocale, currency, withCents: false });

  const displayedCollectedAmount = Math.round(currentCollectedAmount * introProgress);
  const percentage = (currentCollectedAmount / targetedAmount) * 100;
  const displayedPercentage = (displayedCollectedAmount / targetedAmount) * 100;
  const contributionPercentage = targetedAmount > 0 ? (selectedAmount / targetedAmount) * 100 : 0;
  const label = labelProp.renderCurrentAmount(formatAmount(displayedCollectedAmount), formatAmount(targetedAmount));
  const isCampaignAchieved = currentCollectedAmount >= targetedAmount;

  const getMessage = (): FormThermometerMessage | undefined => {
    if (contributionPercentage <= 0 || !labelProp.encouragement) {
      return undefined;
    }

    const tier = getEncouragementTier(contributionPercentage, percentage);
    return { icon: getEncouragementIcon(tier), text: labelProp.encouragement[tier] };
  };

  const thermometerProps = {
    label,
    percentage: displayedPercentage,
    contributionPercentage,
    isProgressTransitioned: introProgress === 1,
  };

  const front = isCampaignAchieved ? (
    <HeartsFormThermometer {...thermometerProps} message={getMessage()} animated loop />
  ) : (
    <FormThermometer {...thermometerProps} message={getMessage()} />
  );

  const getBackLabel = () => {
    if (isCampaignAchieved) {
      return labelProp.campaignAchieved;
    }

    if (percentage >= 80) {
      return labelProp.ongoingCampaign80To99;
    }

    if (percentage >= 50) {
      return labelProp.ongoingCampaign50To79;
    }

    return labelProp.ongoingCampaign0To49;
  };

  const back = <FormThermometer {...thermometerProps} label={getBackLabel()} sx={{ height: "100%" }} />;

  return <FlipCard id={id} front={front} back={back} sx={sx} />;
};

const getEncouragementIcon = (tier: EncouragementTier) => {
  if (tier === "incredible" || tier === "almostThere") {
    return <SparksColored />;
  }
  if (tier === "beautifulStart") {
    return <EnvironmentColored />;
  }
  return <HeartsColored />;
};
